import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Clapperboard,
  Download,
  Film,
  Loader2,
  RefreshCw,
  Upload,
  Users,
  Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import api from '@/lib/api';
import { getIntakeNavPrefill } from '@/lib/intakeNavPrefill';
import { readAgentHandoff } from '@/lib/agentHandoff';
import { AgentHandoffGate } from '@/components/createPlan/AgentHandoffGate';
import { getBackendBaseUrl } from '@/services/config/env';
import { MascotVideoHistoryPanel } from '@/components/mascot/MascotVideoHistoryPanel';
import { MascotStyleTemplatePicker } from '@/components/mascot/MascotStyleTemplatePicker';
import { MascotCharacterTraitsSection } from '@/components/mascot/MascotCharacterTraitsSection';
import {
  DEFAULT_MASCOT_STYLE_BACKGROUND_MODE,
  DEFAULT_MASCOT_STYLE_TEMPLATE_ID,
  MASCOT_BACKGROUND_MODES,
  MASCOT_FLOW_STEPS,
  MASCOT_IMAGE_PROVIDERS,
  dualCharacterPresets,
  historyMediaUrl,
  isHistoryVideoEntry,
  mascotBackgroundPayload,
  mascotMediaUrl,
  mascotAdjacentStep,
  mascotStepFromProject,
  mascotStepProgressIndex,
  canProceedFromStyles,
  mergedExportHistory,
  projectReadyForSheet,
  projectReadyForStyles,
  projectSheetReady,
  projectStylesApplied,
  sheetRowsFromProject,
  singleCharacterPresets,
} from '@/lib/mascotAnimation';

import { TwinneeLogo } from '@/components/TwinneeLogo';
import { agentCreatePath } from '@/lib/creationPlan';
const BACKEND_URL = getBackendBaseUrl();

const POLL_STATUSES = new Set(['generating_sheet', 'rendering_animation', 'rendering_seedance']);

const VALID_STEPS = new Set(MASCOT_FLOW_STEPS.map((s) => s.id));

function stepIndex(stepId) {
  return MASCOT_FLOW_STEPS.findIndex((s) => s.id === stepId);
}

export function MascotAnimationFlowPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId: routeProjectId, step: routeStep } = useParams();
  const pollRef = useRef(null);
  const wasPollingRef = useRef(false);

  const [catalog, setCatalog] = useState({
    shots: [],
    animation_presets: [],
    style_templates: [],
    seedance_durations: [4, 6, 8, 10],
    aspect_ratios: ['16:9', '9:16', '1:1'],
  });
  const [title, setTitle] = useState('');
  const [imageProvider, setImageProvider] = useState('nano_banana');
  const [characterCount, setCharacterCount] = useState(1);
  const [charNames, setCharNames] = useState(['Anchor', 'Co-host']);
  const [project, setProject] = useState(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [uploadingId, setUploadingId] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('smile_hold');
  const [durationSec, setDurationSec] = useState(3);
  const [seedanceDuration, setSeedanceDuration] = useState(8);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [videoPrompt, setVideoPrompt] = useState('');
  const [selectedCharIds, setSelectedCharIds] = useState([]);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [animateMode, setAnimateMode] = useState('seedance');
  const [backgroundMode, setBackgroundMode] = useState('transparent');
  const [backgroundDescription, setBackgroundDescription] = useState('');
  const [backgroundCustomUrl, setBackgroundCustomUrl] = useState('');
  const [styleBackgroundMode, setStyleBackgroundMode] = useState(DEFAULT_MASCOT_STYLE_BACKGROUND_MODE);
  const [styleBackgroundDescription, setStyleBackgroundDescription] = useState('');
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [uploadingStyleBackground, setUploadingStyleBackground] = useState(false);
  const [importingVideo, setImportingVideo] = useState(false);
  const [retryingSeedanceId, setRetryingSeedanceId] = useState('');
  const [styleTemplateId, setStyleTemplateId] = useState(DEFAULT_MASCOT_STYLE_TEMPLATE_ID);
  const [applyingStyleId, setApplyingStyleId] = useState('');
  const [regeneratingCharacterId, setRegeneratingCharacterId] = useState('');
  const [regeneratingShotKey, setRegeneratingShotKey] = useState('');
  const agentHandoff = useMemo(
    () => (routeProjectId ? null : readAgentHandoff(location.state)),
    [location.state, routeProjectId],
  );
  const [handoffReady, setHandoffReady] = useState(false);

  useEffect(() => {
    if (routeProjectId || !readAgentHandoff(location.state)) return;
    const nav = getIntakeNavPrefill(location.state);
    if (nav?.title) setTitle(nav.title);
    setHandoffReady(true);
  }, [location.state, routeProjectId]);

  const currentStep = routeProjectId
    ? (routeStep && VALID_STEPS.has(routeStep) ? routeStep : (project ? mascotStepFromProject(project) : 'upload'))
    : 'setup';

  const sheetRows = useMemo(() => sheetRowsFromProject(project), [project]);
  const charCount = (project?.characters || []).length || characterCount;
  const videoHistory = useMemo(() => mergedExportHistory(project), [project]);

  const availablePresets = useMemo(() => {
    const all = catalog.animation_presets || [];
    const n = selectedCharIds.length || charCount;
    return n >= 2 ? all : singleCharacterPresets(all);
  }, [catalog.animation_presets, charCount, selectedCharIds.length]);

  const dualPresets = useMemo(
    () => dualCharacterPresets(catalog.animation_presets || []),
    [catalog.animation_presets],
  );

  const isSheetGenerating = project?.status === 'generating_sheet';
  const hasActiveVideoJobs = (videoHistory || []).some(
    (h) => h.status === 'running' || h.status === 'pending',
  );
  const isAnimationRendering = project?.status === 'rendering_animation'
    || project?.status === 'rendering_seedance';
  const isGenerating = isSheetGenerating || isAnimationRendering;
  const generatedSheetCharacterIds = useMemo(() => {
    const ids = new Set();
    for (const row of sheetRows) {
      const cutouts = row?.cutouts || {};
      const hasAnyFrame = Object.values(cutouts).some((v) => typeof v === 'string' && v);
      if (hasAnyFrame && row?.characterId) ids.add(row.characterId);
    }
    return ids;
  }, [sheetRows]);
  const canRenderWithPartialSheet = generatedSheetCharacterIds.size > 0;

  const previewUrl = selectedHistory
    ? historyMediaUrl(selectedHistory, BACKEND_URL)
    : mascotMediaUrl(project?.animation?.video_url, BACKEND_URL);

  useEffect(() => {
    if (!selectedHistory?.id) return;
    const latest = videoHistory.find((row) => row.id === selectedHistory.id);
    if (!latest) return;
    if (
      latest.video_url !== selectedHistory.video_url
      || latest.status !== selectedHistory.status
      || latest.source_url !== selectedHistory.source_url
    ) {
      setSelectedHistory(latest);
    }
  }, [selectedHistory, videoHistory]);

  const goToStep = useCallback((stepId) => {
    if (!project?.id) return;
    navigate(`/mascot/${project.id}/${stepId}`);
  }, [navigate, project?.id]);

  const goBackStep = useCallback(() => {
    const prev = mascotAdjacentStep(currentStep, 'back');
    if (prev && project?.id) goToStep(prev);
    else if (project?.id) navigate('/dashboard');
    else navigate(agentCreatePath({ flowId: 'mascot_animation' }));
  }, [currentStep, goToStep, navigate, project?.id]);

  const progressStepIndex = project ? mascotStepProgressIndex(project) : 0;

  const loadCatalog = useCallback(async () => {
    try {
      const { data } = await api.get('/mascot/catalog');
      setCatalog(data);
    } catch {
      /* optional */
    }
  }, []);

  const loadProject = useCallback(async (id) => {
    const { data } = await api.get(`/mascot/projects/${id}`);
    setProject(data.project);
    setTitle(data.project?.title || '');
    setImageProvider(data.project?.image_provider || 'nano_banana');
    setBackgroundCustomUrl(data.project?.animation_background_custom_url || '');
    const chars = data.project?.characters || [];
    const savedStyleBg = data.project?.style_background;
    if (savedStyleBg?.mode) {
      setStyleBackgroundMode(savedStyleBg.mode);
      setStyleBackgroundDescription(savedStyleBg.description || '');
    } else {
      const charStyleBg = chars.find((c) => c.style_background?.mode)?.style_background;
      if (charStyleBg?.mode) {
        setStyleBackgroundMode(charStyleBg.mode);
        setStyleBackgroundDescription(charStyleBg.description || '');
      }
    }
    setSelectedCharIds(chars.map((c) => c.id));
    const withStyle = chars.find((c) => c.style_template_id);
    if (withStyle?.style_template_id) {
      setStyleTemplateId(withStyle.style_template_id);
    } else if (data.project?.default_style_template_id) {
      setStyleTemplateId(data.project.default_style_template_id);
    }
    if (data.project?.animation?.preset_id) {
      setSelectedPreset(data.project.animation.preset_id);
    }
    return data.project;
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    if (!routeProjectId) {
      setProject(null);
      setProjectLoading(false);
      return undefined;
    }
    let cancelled = false;
    setProjectLoading(true);
    (async () => {
      try {
        const p = await loadProject(routeProjectId);
        if (cancelled) return;
        if (!routeStep) {
          navigate(`/mascot/${routeProjectId}/${mascotStepFromProject(p)}`, { replace: true });
        } else if (!VALID_STEPS.has(routeStep)) {
          navigate(`/mascot/${routeProjectId}/${mascotStepFromProject(p)}`, { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || 'Could not load project');
          navigate(agentCreatePath({ flowId: 'mascot_animation' }), { replace: true });
        }
      } finally {
        if (!cancelled) setProjectLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeProjectId, routeStep, loadProject, navigate]);

  useEffect(() => {
    if (!project?.id) return undefined;
    const hist = mergedExportHistory(project);
    const needsPoll = POLL_STATUSES.has(project.status)
      || hist.some((h) => h.status === 'running' || h.status === 'pending');
    if (!needsPoll) {
      if (wasPollingRef.current) {
        wasPollingRef.current = false;
        loadProject(project.id).catch(() => {});
      }
      return undefined;
    }

    wasPollingRef.current = true;
    pollRef.current = setInterval(async () => {
      try {
        await loadProject(project.id);
      } catch {
        /* ignore */
      }
    }, 4000);

    return () => clearInterval(pollRef.current);
  }, [project?.id, project?.status, project?.output_history, project?.export_history, project?.video_history, project?.character_sheets, loadProject]);

  const createProject = async () => {
    if (!title.trim()) {
      toast.error('Enter a project title');
      return;
    }
    const names = charNames.slice(0, characterCount).map((n, i) => (n.trim() || `Character ${i + 1}`));
    setLoading(true);
    try {
      const { data } = await api.post('/mascot/projects', {
        title: title.trim(),
        image_provider: imageProvider,
        characters: names.map((name) => ({ name })),
      });
      setProject(data.project);
      navigate(`/mascot/${data.project.id}/upload`, { replace: true });
      toast.success('Mascot project created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const uploadReference = async (characterId, file) => {
    if (!project?.id || !file) return;
    setUploadingId(characterId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(
        `/mascot/projects/${project.id}/characters/${characterId}/upload`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setProject(data.project);
      const ch = data.project?.characters?.find((c) => c.id === characterId);
      if (ch?.appearance_profile?.analysis_status === 'completed') {
        toast.success('Reference uploaded — character traits detected for consistency');
      } else {
        toast.success('Reference photo uploaded');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingId('');
    }
  };

  const styleBackgroundPayload = () => mascotBackgroundPayload(
    styleBackgroundMode,
    styleBackgroundDescription,
    backgroundCustomUrl,
  );

  const validateStyleBackground = () => {
    if (styleBackgroundMode === 'describe' && !styleBackgroundDescription.trim()) {
      toast.error('Describe the background or pick another option');
      return false;
    }
    if (styleBackgroundMode === 'custom_image' && !backgroundCustomUrl) {
      toast.error('Upload a custom background image first');
      return false;
    }
    return true;
  };

  const applyStyleToCharacter = async (characterId) => {
    if (!project?.id || !styleTemplateId) return;
    if (!validateStyleBackground()) return;
    setApplyingStyleId(characterId);
    try {
      const fd = new FormData();
      fd.append('style_template_id', styleTemplateId);
      fd.append('background', JSON.stringify(styleBackgroundPayload()));
      const { data } = await api.post(
        `/mascot/projects/${project.id}/characters/${characterId}/apply-style`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setProject(data.project);
      toast.success('Style template applied');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Style conversion failed');
    } finally {
      setApplyingStyleId('');
    }
  };

  const applyStyleToAll = async () => {
    if (!project?.id) return;
    const chars = (project.characters || []).filter((c) => c.reference_url);
    if (!chars.length) {
      toast.error('Upload reference photos first');
      return;
    }
    if (!styleTemplateId) {
      toast.error('Select a style template or skip styling');
      return;
    }
    if (!validateStyleBackground()) return;
    setApplyingStyleId('all');
    try {
      let latest = project;
      for (const ch of chars) {
        setApplyingStyleId(ch.id);
        const fd = new FormData();
        fd.append('style_template_id', styleTemplateId);
        fd.append('background', JSON.stringify(styleBackgroundPayload()));
        const { data } = await api.post(
          `/mascot/projects/${project.id}/characters/${ch.id}/apply-style`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        latest = data.project;
        setProject(data.project);
      }
      setProject(latest);
      toast.success('Style applied to all characters');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Style conversion failed');
    } finally {
      setApplyingStyleId('');
    }
  };

  const skipStyles = async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/mascot/projects/${project.id}/skip-styles`);
      setProject(data.project);
      goToStep('sheet');
      toast.info('Using original photos for character sheet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not skip style step');
    } finally {
      setLoading(false);
    }
  };

  const generateSheet = async ({ regenerate = false } = {}) => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/mascot/projects/${project.id}/generate-sheet`);
      setProject(data.project);
      goToStep('sheet');
      toast.info(
        regenerate
          ? 'Regenerating all character sheets with current traits'
          : 'Generating character sheet — several minutes per character',
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start sheet generation');
    } finally {
      setLoading(false);
    }
  };

  const regenerateCharacterSheet = async (characterId) => {
    if (!project?.id || !characterId) return;
    setRegeneratingCharacterId(characterId);
    try {
      const { data } = await api.post(
        `/mascot/projects/${project.id}/characters/${characterId}/generate-sheet`,
      );
      setProject(data.project);
      toast.info('Regenerating character sheet with current traits');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not regenerate character sheet');
    } finally {
      setRegeneratingCharacterId('');
    }
  };

  const regenerateSheetShot = async (characterId, shotKey) => {
    if (!project?.id || !characterId || !shotKey) return;
    const token = `${characterId}:${shotKey}`;
    setRegeneratingShotKey(token);
    try {
      const { data } = await api.post(
        `/mascot/projects/${project.id}/characters/${characterId}/regenerate-shot`,
        { shot_key: shotKey },
      );
      setProject(data.project);
      toast.success('Pose image regenerated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not regenerate pose');
    } finally {
      setRegeneratingShotKey('');
    }
  };

  const skipSheet = async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/mascot/projects/${project.id}/skip-sheet`);
      setProject(data.project);
      goToStep('animate');
      toast.info('Skipping character sheet — using your photos for animation');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not skip character sheet');
    } finally {
      setLoading(false);
    }
  };

  const toggleCharId = (id) => {
    setSelectedCharIds((prev) => {
      if (prev.includes(id)) {
        return prev.length > 1 ? prev.filter((x) => x !== id) : prev;
      }
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
  };

  const uploadBackgroundImage = async (file, target = 'animate') => {
    if (!project?.id || !file) return;
    const setUploading = target === 'style' ? setUploadingStyleBackground : setUploadingBackground;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(
        `/mascot/projects/${project.id}/upload-background`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setProject(data.project);
      const url = data.custom_image_url || data.project?.animation_background_custom_url || '';
      setBackgroundCustomUrl(url);
      if (target === 'style') {
        setStyleBackgroundMode('custom_image');
      } else {
        setBackgroundMode('custom_image');
      }
      toast.success('Background image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Background upload failed');
    } finally {
      setUploading(false);
    }
  };

  const renderWebm = async () => {
    if (!project?.id || !selectedPreset) return;
    const renderableCharIds = (selectedCharIds || []).filter((id) => generatedSheetCharacterIds.has(id));
    if (!renderableCharIds.length) {
      toast.error('Generate at least one character sheet frame before rendering');
      return;
    }
    if (backgroundMode === 'describe' && !backgroundDescription.trim()) {
      toast.error('Describe the background or pick another option');
      return;
    }
    if (backgroundMode === 'custom_image' && !backgroundCustomUrl) {
      toast.error('Upload a custom background image first');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/mascot/projects/${project.id}/render-animation`, {
        preset_id: selectedPreset,
        duration_sec: durationSec,
        character_ids: renderableCharIds,
        background: mascotBackgroundPayload(backgroundMode, backgroundDescription, backgroundCustomUrl),
      });
      setProject(data.project);
      const hist = mergedExportHistory(data.project);
      if (hist[0]) setSelectedHistory(hist[0]);
      goToStep('export');
      toast.success(backgroundMode === 'transparent' ? 'Transparent WebM ready' : 'Animation video ready');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Animation render failed');
    } finally {
      setLoading(false);
    }
  };

  const renderSeedance = async () => {
    if (!project?.id || !selectedPreset) return;
    const renderableCharIds = (selectedCharIds || []).filter((id) => generatedSheetCharacterIds.has(id));
    if (!renderableCharIds.length) {
      toast.error('Select at least one character');
      return;
    }
    if (backgroundMode === 'describe' && !backgroundDescription.trim()) {
      toast.error('Describe the background or pick another option');
      return;
    }
    if (backgroundMode === 'custom_image' && !backgroundCustomUrl) {
      toast.error('Upload a custom background image first');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/mascot/projects/${project.id}/render-seedance`, {
        preset_id: selectedPreset,
        character_ids: renderableCharIds,
        video_prompt: videoPrompt.trim(),
        duration_sec: seedanceDuration,
        aspect_ratio: aspectRatio,
        background: mascotBackgroundPayload(backgroundMode, backgroundDescription, backgroundCustomUrl),
        runway_model: animateMode === 'seedance_2_5' || animateMode === 'fable_5_1' ? 'seedance2_5' : 'seedance2',
      });
      setProject(data.project);
      setSelectedHistory(data.history_entry || mergedExportHistory(data.project)[0] || null);
      goToStep('export');
      await loadProject(project.id);
      toast.info(
        animateMode === 'seedance_2_5' || animateMode === 'fable_5_1'
          ? 'Seedance 2.5 started — check history panel for progress'
          : 'Seedance 2 started — check history panel for progress'
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Seedance render failed');
    } finally {
      setLoading(false);
    }
  };

  const refreshProject = async () => {
    if (!project?.id) return;
    setRefreshing(true);
    try {
      await loadProject(project.id);
    } finally {
      setRefreshing(false);
    }
  };

  const retrySeedance = async (entry) => {
    if (!project?.id || !entry?.id) return;
    setRetryingSeedanceId(entry.id);
    try {
      const { data } = await api.post(
        `/mascot/projects/${project.id}/video-history/${entry.id}/retry-seedance`,
      );
      setProject(data.project);
      const hist = mergedExportHistory(data.project);
      const retried = hist.find((h) => h.id === entry.id) || hist[0];
      if (retried) setSelectedHistory(retried);
      toast.info('Retrying Seedance 2 — check history for progress');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not retry Seedance render');
    } finally {
      setRetryingSeedanceId('');
    }
  };

  const importVideoUrl = async (sourceUrl) => {
    if (!project?.id || !sourceUrl) return;
    setImportingVideo(true);
    try {
      const { data } = await api.post(`/mascot/projects/${project.id}/import-video`, {
        source_url: sourceUrl,
        kind: 'seedance2',
        preset_label: 'Seedance 2 (imported)',
      });
      setProject(data.project);
      const hist = mergedExportHistory(data.project);
      const imported = hist.find((h) => h.id === data.history_entry_id) || hist[0];
      if (imported) setSelectedHistory(imported);
      goToStep('export');
      toast.success('Video imported into history');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not import video URL');
    } finally {
      setImportingVideo(false);
    }
  };

  const stepNav = (
    <div className="flex flex-wrap gap-2 mb-6">
      {MASCOT_FLOW_STEPS.map((s) => {
        const idx = stepIndex(s.id);
        const active = s.id === currentStep;
        const done = project ? progressStepIndex >= idx : idx === 0;
        const clickable = project?.id ? done : !routeProjectId && s.id === 'setup';
        return (
          <button
            key={s.id}
            type="button"
            disabled={!clickable || projectLoading}
            onClick={() => clickable && (project?.id ? goToStep(s.id) : null)}
            className="text-xs px-3 py-1.5 rounded-full border transition-colors"
            style={{
              borderColor: active || done ? '#0EA5E9' : 'var(--glass-border)',
              color: active ? '#0EA5E9' : done ? '#38BDF8' : 'var(--text-tertiary)',
              background: active ? 'rgba(14,165,233,0.12)' : 'transparent',
              cursor: clickable && !projectLoading ? 'pointer' : 'default',
            }}
          >
            {idx + 1}. {s.label}
          </button>
        );
      })}
    </div>
  );

  const stepBackButton = (label = 'Back') => (
    <Button type="button" variant="outline" className="rounded-full" onClick={goBackStep}>
      <ArrowLeft className="w-4 h-4 mr-1" />
      {label}
    </Button>
  );

  const mainContent = (
    <div className="flex-1 min-w-0 space-y-6">
      {stepNav}

      {routeProjectId && projectLoading && (
        <div className="glass-panel rounded-3xl p-8 border border-[var(--glass-border)] flex items-center justify-center gap-2 text-sm text-[var(--text-secondary)]">
          <Loader2 className="w-4 h-4 animate-spin text-sky-500" />
          Loading project…
        </div>
      )}

      {currentStep === 'setup' && !routeProjectId && !project && (
        <div className="glass-panel rounded-3xl p-6 sm:p-8 space-y-5 border border-[var(--glass-border)]">
          <div className="space-y-2">
            <Label>Project title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Evening News mascots" data-testid="mascot-title-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Image provider</Label>
              <Select value={imageProvider} onValueChange={setImageProvider}>
                <SelectTrigger data-testid="mascot-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MASCOT_IMAGE_PROVIDERS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Characters</Label>
              <Select value={String(characterCount)} onValueChange={(v) => setCharacterCount(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 character</SelectItem>
                  <SelectItem value="2">2 characters</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {Array.from({ length: characterCount }).map((_, i) => (
              <div key={i} className="space-y-1">
                <Label>Character {i + 1} name</Label>
                <Input
                  value={charNames[i] || ''}
                  onChange={(e) => {
                    const next = [...charNames];
                    next[i] = e.target.value;
                    setCharNames(next);
                  }}
                />
              </div>
            ))}
          </div>
          <Button onClick={createProject} disabled={loading} className="rounded-full" data-testid="mascot-create-project-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TwinneeLogo className="w-4 h-4 mr-2" />}
            Create project
          </Button>
        </div>
      )}

      {project && currentStep === 'setup' && (
        <section className="glass-panel rounded-3xl p-6 sm:p-8 space-y-5 border border-[var(--glass-border)]">
          <h2 className="font-heading font-bold text-lg">Project overview</h2>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[var(--text-tertiary)] text-xs mb-1">Title</p>
              <p className="font-medium">{project.title}</p>
            </div>
            <div>
              <p className="text-[var(--text-tertiary)] text-xs mb-1">Image provider</p>
              <p className="font-medium">
                {MASCOT_IMAGE_PROVIDERS.find((p) => p.value === project.image_provider)?.label || project.image_provider}
              </p>
            </div>
          </div>
          <div>
            <p className="text-[var(--text-tertiary)] text-xs mb-2">Characters</p>
            <ul className="space-y-1">
              {(project.characters || []).map((ch) => (
                <li key={ch.id} className="text-sm font-medium">{ch.name}</li>
              ))}
            </ul>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button className="rounded-full" onClick={() => goToStep(mascotStepFromProject(project))}>
              Continue <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            {stepBackButton('Back to dashboard')}
          </div>
        </section>
      )}

      {project && currentStep === 'upload' && (
        <section className="glass-panel rounded-3xl p-6 border border-[var(--glass-border)]">
          <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-sky-500" />
            Reference photos
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mb-4">
            Upload a clear photo for each character. You will pick a cartoon style on the next step.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {(project.characters || []).map((ch) => (
              <div key={ch.id} className="rounded-2xl border border-[var(--glass-border)] p-4">
                <p className="font-medium mb-2">{ch.name}</p>
                {ch.reference_url ? (
                  <img src={mascotMediaUrl(ch.reference_url, BACKEND_URL)} alt={ch.name} className="w-full aspect-square object-cover rounded-xl mb-3 bg-slate-100 dark:bg-slate-800" />
                ) : (
                  <div className="aspect-square rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3 text-sm text-[var(--text-tertiary)]">No photo</div>
                )}
                <input
                  id={`mascot-upload-${ch.id}`}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadReference(ch.id, f);
                    e.target.value = '';
                  }}
                />
                <Button type="button" variant="outline" className="w-full" disabled={uploadingId === ch.id} onClick={() => document.getElementById(`mascot-upload-${ch.id}`)?.click()}>
                  {uploadingId === ch.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {ch.reference_url ? 'Replace' : 'Upload'}
                </Button>
                <MascotCharacterTraitsSection
                  projectId={project.id}
                  character={ch}
                  onUpdated={setProject}
                  disabled={uploadingId === ch.id || loading}
                />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {stepBackButton()}
            <Button
              className="rounded-full"
              onClick={() => goToStep('styles')}
              disabled={!projectReadyForStyles(project)}
            >
              Next: choose style <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </section>
      )}

      {project && currentStep === 'styles' && (
        <section className="glass-panel rounded-3xl p-6 border border-[var(--glass-border)]">
          <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-sky-500" />
            Cartoon style
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mb-4">
            Pick a Fotor template, then apply it to your uploaded photos before generating the character sheet.
          </p>
          <div className="mb-6">
            <MascotStyleTemplatePicker
              templates={catalog.style_templates || []}
              value={styleTemplateId}
              onChange={setStyleTemplateId}
              disabled={Boolean(applyingStyleId) || loading}
              imageProvider={project.image_provider || imageProvider}
            />
          </div>

          <div className="space-y-3 rounded-2xl border border-[var(--glass-border)] p-4 mb-6">
            <Label>Styled photo background</Label>
            <Select value={styleBackgroundMode} onValueChange={setStyleBackgroundMode}>
              <SelectTrigger data-testid="mascot-style-background-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MASCOT_BACKGROUND_MODES.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[var(--text-tertiary)]">
              {(MASCOT_BACKGROUND_MODES.find((o) => o.value === styleBackgroundMode) || {}).styleHint
                || (MASCOT_BACKGROUND_MODES.find((o) => o.value === styleBackgroundMode) || {}).hint}
            </p>

            {styleBackgroundMode === 'describe' ? (
              <div className="space-y-2">
                <Label>Background description</Label>
                <Textarea
                  value={styleBackgroundDescription}
                  onChange={(e) => setStyleBackgroundDescription(e.target.value)}
                  placeholder="e.g. Soft pastel news studio with warm lighting…"
                  className="min-h-[72px]"
                  data-testid="mascot-style-background-description"
                />
              </div>
            ) : null}

            {styleBackgroundMode === 'custom_image' ? (
              <div className="space-y-2">
                {backgroundCustomUrl ? (
                  <img
                    src={mascotMediaUrl(backgroundCustomUrl, BACKEND_URL)}
                    alt="Custom style background"
                    className="w-full max-h-32 object-cover rounded-lg border"
                  />
                ) : null}
                <input
                  id="mascot-style-bg-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadBackgroundImage(f, 'style');
                    e.target.value = '';
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={uploadingStyleBackground}
                  onClick={() => document.getElementById('mascot-style-bg-upload')?.click()}
                >
                  {uploadingStyleBackground ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {backgroundCustomUrl ? 'Replace background image' : 'Upload background image'}
                </Button>
              </div>
            ) : null}
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            {(project.characters || []).map((ch) => (
              <div key={ch.id} className="rounded-2xl border border-[var(--glass-border)] p-4">
                <p className="font-medium mb-2">{ch.name}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[10px] text-[var(--text-tertiary)] mb-1">Original</p>
                    {ch.reference_url ? (
                      <img src={mascotMediaUrl(ch.reference_url, BACKEND_URL)} alt={`${ch.name} original`} className="w-full aspect-square object-cover rounded-xl bg-slate-100 dark:bg-slate-800" />
                    ) : (
                      <div className="aspect-square rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] text-[var(--text-tertiary)]">No photo</div>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] text-[var(--text-tertiary)] mb-1">Styled</p>
                    {ch.styled_reference_url ? (
                      <img src={mascotMediaUrl(ch.styled_reference_url, BACKEND_URL)} alt={`${ch.name} styled`} className="w-full aspect-square object-cover rounded-xl bg-slate-100 dark:bg-slate-800" />
                    ) : (
                      <div className="aspect-square rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] text-[var(--text-tertiary)] px-2 text-center">
                        {applyingStyleId === ch.id || applyingStyleId === 'all' ? (
                          <Loader2 className="w-4 h-4 animate-spin text-sky-500" />
                        ) : ch.style_status === 'failed' ? 'Style failed' : 'Not applied'}
                      </div>
                    )}
                  </div>
                </div>
                {ch.reference_url && styleTemplateId ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full text-xs mt-2"
                    disabled={Boolean(applyingStyleId) || loading}
                    onClick={() => applyStyleToCharacter(ch.id)}
                  >
                    {applyingStyleId === ch.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                    Apply style
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {stepBackButton()}
            {styleTemplateId ? (
              <Button
                className="rounded-full"
                onClick={applyStyleToAll}
                disabled={Boolean(applyingStyleId) || loading || !projectReadyForStyles(project)}
              >
                {applyingStyleId ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                Apply to all characters
              </Button>
            ) : null}
            <Button
              variant="outline"
              className="rounded-full"
              onClick={skipStyles}
              disabled={loading || Boolean(applyingStyleId)}
            >
              Skip styling
            </Button>
            <Button
              className="rounded-full"
              onClick={() => goToStep('sheet')}
              disabled={!canProceedFromStyles(project) || loading}
            >
              Next: character sheet <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={skipSheet}
              disabled={!canProceedFromStyles(project) || isSheetGenerating || loading || Boolean(applyingStyleId)}
            >
              Skip character sheet
            </Button>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => generateSheet()}
              disabled={!canProceedFromStyles(project) || isSheetGenerating || loading || Boolean(applyingStyleId)}
            >
              Generate character sheet
            </Button>
          </div>
          {!canProceedFromStyles(project) && !applyingStyleId ? (
            <p className="text-[10px] text-[var(--text-tertiary)] mt-3">
              Apply style to each character, or use Skip styling to continue with your original photos.
            </p>
          ) : null}
        </section>
      )}

      {project && currentStep === 'sheet' && (
        <section className="glass-panel rounded-3xl p-6 border border-[var(--glass-border)]">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-sky-500" />
              Character sheet
            </h2>
            {projectSheetReady(project) && !project.sheet_step_skipped && project.status !== 'generating_sheet' ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full"
                disabled={loading || isSheetGenerating || Boolean(regeneratingCharacterId) || Boolean(regeneratingShotKey)}
                onClick={() => generateSheet({ regenerate: true })}
              >
                {isSheetGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Regenerate all sheets
              </Button>
            ) : null}
          </div>
          {project.sheet_step_skipped ? (
            <div className="space-y-4">
              <p className="text-sm text-[var(--text-secondary)]">
                Character sheet generation was skipped. Animation will use your styled or original reference photo for all poses.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {(project.characters || []).map((ch) => {
                  const previewUrl = ch.styled_reference_url || ch.reference_url;
                  return (
                    <div key={ch.id} className="rounded-2xl border border-[var(--glass-border)] p-4">
                      <p className="font-medium mb-2">{ch.name}</p>
                      <MascotCharacterTraitsSection
                        projectId={project.id}
                        character={ch}
                        onUpdated={setProject}
                        disabled={loading || isSheetGenerating}
                        className="mb-3"
                      />
                      {previewUrl ? (
                        <img
                          src={mascotMediaUrl(previewUrl, BACKEND_URL)}
                          alt={`${ch.name} reference`}
                          className="w-full aspect-square object-cover rounded-xl bg-slate-100 dark:bg-slate-800"
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <Button
                type="button"
                className="rounded-full"
                onClick={() => generateSheet()}
                disabled={loading || isSheetGenerating || !projectReadyForSheet(project)}
              >
                {isSheetGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                Generate character sheet
              </Button>
            </div>
          ) : sheetRows.length ? sheetRows.map((row) => {
            const charMeta = (project.characters || []).find((c) => c.id === row.characterId);
            return (
            <div key={row.characterId} className="mb-6 last:mb-0">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <p className="font-medium">{row.name}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-[10px] rounded-full"
                  disabled={
                    loading
                    || isSheetGenerating
                    || Boolean(regeneratingCharacterId)
                    || Boolean(regeneratingShotKey)
                  }
                  onClick={() => regenerateCharacterSheet(row.characterId)}
                >
                  {regeneratingCharacterId === row.characterId ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3 h-3 mr-1" />
                  )}
                  Regenerate sheet
                </Button>
              </div>
              {charMeta ? (
                <MascotCharacterTraitsSection
                  projectId={project.id}
                  character={charMeta}
                  onUpdated={setProject}
                  disabled={loading || isSheetGenerating || regeneratingCharacterId === row.characterId}
                  className="mb-3"
                />
              ) : null}
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {(catalog.shots || []).map((shot) => {
                  const src = mascotMediaUrl(row.cutouts?.[shot.key], BACKEND_URL);
                  const shotBusy = regeneratingShotKey === `${row.characterId}:${shot.key}`;
                  return (
                    <div key={shot.key} className="rounded-lg overflow-hidden border bg-[repeating-conic-gradient(#e2e8f0_0%_25%,#fff_0%_50%)] bg-[length:12px_12px] relative group">
                      <div className="aspect-square">
                        {shotBusy ? (
                          <div className="w-full h-full flex items-center justify-center bg-white/70">
                            <Loader2 className="w-4 h-4 animate-spin text-sky-500" />
                          </div>
                        ) : src ? (
                          <img src={src} alt={shot.label} className="w-full h-full object-contain" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[10px] text-slate-400">…</div>
                        )}
                      </div>
                      <p className="text-[9px] text-center truncate px-0.5 py-0.5 bg-white/90">{shot.label}</p>
                      <Button
                        type="button"
                        variant="secondary"
                        size="icon"
                        className="absolute top-1 right-1 z-10 h-6 w-6 opacity-90 shadow-sm"
                        title={`Regenerate ${shot.label}`}
                        disabled={
                          loading
                          || isSheetGenerating
                          || Boolean(regeneratingCharacterId)
                          || Boolean(regeneratingShotKey)
                        }
                        onClick={() => regenerateSheetShot(row.characterId, shot.key)}
                      >
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
            );
          }) : project.status === 'generating_sheet' ? (
            <p className="text-sm text-[var(--text-tertiary)] flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-sky-500" />
              Generating character sheet — this can take several minutes per character…
            </p>
          ) : (
            <p className="text-sm text-[var(--text-tertiary)]">Generate pose sheets from the Style step, or skip to animate with your reference photos.</p>
          )}
          <div className="flex flex-wrap gap-2 mt-4">
            {stepBackButton()}
            {!project.sheet_step_skipped && project.status !== 'generating_sheet' && !projectSheetReady(project) ? (
              <>
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={skipSheet}
                  disabled={loading || isSheetGenerating}
                >
                  Skip character sheet
                </Button>
                <Button
                  className="rounded-full"
                  onClick={() => generateSheet()}
                  disabled={loading || isSheetGenerating || !projectReadyForSheet(project)}
                >
                  Generate character sheet
                </Button>
              </>
            ) : null}
            <Button className="rounded-full" onClick={() => goToStep('animate')} disabled={!projectSheetReady(project)}>
            Continue to animate <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </section>
      )}

      {project && currentStep === 'animate' && (
        <section className="glass-panel rounded-3xl p-6 border border-[var(--glass-border)] space-y-5">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2">
            <Clapperboard className="w-5 h-5 text-sky-500" />
            Animation
          </h2>

          <div className="space-y-2">
            <Label>Characters to animate</Label>
            <div className="flex flex-wrap gap-3">
              {(project.characters || []).map((ch) => (
                <label key={ch.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={selectedCharIds.includes(ch.id)} onCheckedChange={() => toggleCharId(ch.id)} />
                  {ch.name}
                </label>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Preset</Label>
              <Select value={selectedPreset} onValueChange={setSelectedPreset}>
                <SelectTrigger data-testid="mascot-preset-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availablePresets.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCharIds.length < 2 && dualPresets.length > 0 && (
                <p className="text-[10px] text-[var(--text-tertiary)]">Select 2 characters for handshake / fist bump.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Output mode</Label>
              <Select value={animateMode} onValueChange={setAnimateMode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="seedance">Seedance 2 (Runway) — AI video</SelectItem>
                  <SelectItem value="seedance_2_5">Seedance 2.5 (Runway) — AI video</SelectItem>
                  <SelectItem value="chatgpt_astra">ChatGPT Astra (OpenAI Video)</SelectItem>
                  <SelectItem value="fable_5_1">Fable 5.1 (via Seedance 2.5)</SelectItem>
                  <SelectItem value="webm">Quick transparent WebM</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3 rounded-2xl border border-[var(--glass-border)] p-4">
            <Label>Video background</Label>
            <Select value={backgroundMode} onValueChange={setBackgroundMode}>
              <SelectTrigger data-testid="mascot-background-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MASCOT_BACKGROUND_MODES.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[var(--text-tertiary)]">
              {(MASCOT_BACKGROUND_MODES.find((o) => o.value === backgroundMode) || {}).hint}
            </p>

            {backgroundMode === 'describe' ? (
              <div className="space-y-2">
                <Label>Background description</Label>
                <Textarea
                  value={backgroundDescription}
                  onChange={(e) => setBackgroundDescription(e.target.value)}
                  placeholder="e.g. Modern TV news studio with blue LED panels and soft floor reflection…"
                  className="min-h-[72px]"
                  data-testid="mascot-background-description"
                />
              </div>
            ) : null}

            {backgroundMode === 'custom_image' ? (
              <div className="space-y-2">
                {backgroundCustomUrl ? (
                  <img
                    src={mascotMediaUrl(backgroundCustomUrl, BACKEND_URL)}
                    alt="Custom background"
                    className="w-full max-h-32 object-cover rounded-lg border"
                  />
                ) : null}
                <input
                  id="mascot-bg-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadBackgroundImage(f);
                    e.target.value = '';
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={uploadingBackground}
                  onClick={() => document.getElementById('mascot-bg-upload')?.click()}
                >
                  {uploadingBackground ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {backgroundCustomUrl ? 'Replace background image' : 'Upload background image'}
                </Button>
              </div>
            ) : null}
          </div>

          {['seedance', 'seedance_2_5', 'fable_5_1'].includes(animateMode) ? (
            <>
              <div className="space-y-2">
                <Label>Video details (motion, mood, camera)</Label>
                <Textarea
                  value={videoPrompt}
                  onChange={(e) => setVideoPrompt(e.target.value)}
                  placeholder="e.g. Slow friendly wave toward camera, soft studio lighting, news bumper energy…"
                  className="min-h-[88px]"
                  data-testid="mascot-seedance-prompt"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Duration</Label>
                  <Select value={String(seedanceDuration)} onValueChange={(v) => setSeedanceDuration(Number(v))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(catalog.seedance_durations || [4, 6, 8, 10]).map((n) => (
                        <SelectItem key={n} value={String(n)}>{n}s</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Aspect ratio</Label>
                  <Select value={aspectRatio} onValueChange={setAspectRatio}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(catalog.aspect_ratios || ['16:9', '9:16', '1:1']).map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                {stepBackButton()}
                <Button onClick={renderSeedance} disabled={loading || !canRenderWithPartialSheet} className="rounded-full" data-testid="mascot-render-seedance-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Film className="w-4 h-4 mr-2" />}
                  Generate with Seedance 2
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2 max-w-xs">
                <Label>Duration (seconds)</Label>
                <Select value={String(durationSec)} onValueChange={(v) => setDurationSec(Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2, 3, 4, 5].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}s</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-2">
                {stepBackButton()}
                <Button onClick={renderWebm} disabled={loading || !canRenderWithPartialSheet} className="rounded-full" data-testid="mascot-render-animation-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Film className="w-4 h-4 mr-2" />}
                  {backgroundMode === 'transparent' ? 'Render transparent WebM' : 'Render animation video'}
                </Button>
              </div>
            </>
          )}
        </section>
      )}

      {project && currentStep === 'export' && (
        <section className="glass-panel rounded-3xl p-6 border border-[var(--glass-border)]">
          <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
            <Download className="w-5 h-5 text-sky-500" />
            Preview & export
          </h2>
          {previewUrl ? (
            <>
              <div
                className="rounded-2xl overflow-hidden border mb-4 w-full"
                style={{
                  background: selectedHistory?.kind === 'local_webm'
                    ? 'repeating-conic-gradient(#334155 0% 25%, #1e293b 0% 50%)'
                    : selectedHistory?.background?.mode === 'green'
                      ? '#00FF00'
                      : selectedHistory?.background?.mode === 'white'
                        ? '#FFFFFF'
                        : '#0f172a',
                  backgroundSize: selectedHistory?.kind === 'local_webm' ? '20px 20px' : undefined,
                }}
              >
                <video src={previewUrl} className="w-full aspect-video object-contain" autoPlay loop muted playsInline data-testid="mascot-animation-preview" />
              </div>
              <Button asChild className="rounded-full">
                <a href={previewUrl} download target="_blank" rel="noreferrer">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </a>
              </Button>
            </>
          ) : (
            <p className="text-sm text-[var(--text-tertiary)]">Pick a completed render from the history panel, or run an animation.</p>
          )}
          <div className="flex flex-wrap gap-2 mt-4">
            {stepBackButton('Back to animate')}
          </div>
        </section>
      )}
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col lg:flex-row w-full" data-testid="mascot-flow-page">
      <AgentHandoffGate
        handoff={agentHandoff}
        ready={handoffReady}
        flowLabel="Mascot animation"
        busy={loading}
        onStart={createProject}
        startLabel="Create project"
        fields={[
          {
            id: 'title',
            label: 'Project title',
            valid: Boolean(title.trim()),
            render: () => (
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Channel mascot intro" autoFocus />
            ),
          },
        ]}
      />
      <div className="flex-1 min-w-0 px-4 sm:px-6 py-8 overflow-y-auto">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-5xl">
          <div className="mb-6">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mb-3 -ml-2 text-[var(--text-secondary)]"
              onClick={() => navigate(project?.id ? '/dashboard' : agentCreatePath({ flowId: 'mascot_animation' }))}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {project?.id ? 'Back to dashboard' : 'Mascot home'}
            </Button>
            <h1 className="font-heading font-extrabold text-3xl tracking-tight">
              <span className="gradient-text">News Mascot</span> Animation
            </h1>
            <p className="text-[var(--text-secondary)] mt-2 text-sm">
              Multipage flow: photos → cartoon style → green-screen sheet → Seedance 2 or transparent WebM.
            </p>
            {project && isGenerating && (
              <p className="text-xs text-sky-500 mt-2 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                {project.progress_message || 'Working…'}
              </p>
            )}
          </div>
          {mainContent}
        </motion.div>
      </div>

      {project ? (
        <MascotVideoHistoryPanel
          history={videoHistory}
          backendUrl={BACKEND_URL}
          selectedId={selectedHistory?.id}
          onSelect={(row) => {
            setSelectedHistory(row);
            if (row.status === 'completed' && isHistoryVideoEntry(row)) goToStep('export');
          }}
          onRefresh={refreshProject}
          loading={refreshing}
          onImportUrl={importVideoUrl}
          importing={importingVideo}
          onRetrySeedance={retrySeedance}
          retryingId={retryingSeedanceId}
        />
      ) : null}
    </div>
  );
}

export default MascotAnimationFlowPage;
